// Key must be in capitalised Snake case to match the error codes returned by the API
export enum ApiErrorCode {
  DUPLICATE_EMAIL,
  DUPLICATE_FIELD,
  FIELD_SIZE_EXCEEDED,
  AMOUNT_TOO_LARGE,
  UNKNOWN_ERROR,
  ENTITY_HAS_DEPENDENCIES,
  FILE_TOO_LARGE,
  UNSUPPORTED_FILE_EXTENSION,
  CLIENT_ALREADY_IN_FAMILY,
  UNPARSABLE_RESPONSE,
}

// Errors that any mutation may surface and that no individual caller is expected to handle.
// A global mutation handler (see App.tsx) shows these to the user in a modal, so put a code
// here when the right response is the same everywhere and the calling component has no
// better, more specific message of its own.
const GLOBALLY_HANDLED_CODES = new Set([
  ApiErrorCode.ENTITY_HAS_DEPENDENCIES,
  ApiErrorCode.FILE_TOO_LARGE,
  ApiErrorCode.UNSUPPORTED_FILE_EXTENSION,
]);

// Errors the calling component handles locally and shows the user a clear message for
// (e.g. a form field error). Reporting these to Sentry would be noise — they indicate
// invalid input, not unexpected system failures, so they are suppressed there. Put a code
// here when a specific caller owns the user-facing handling.
const EXPECTED_VALIDATION_CODES = new Set([
  ApiErrorCode.DUPLICATE_EMAIL,
  ApiErrorCode.DUPLICATE_FIELD,
  ApiErrorCode.FIELD_SIZE_EXCEEDED,
  ApiErrorCode.AMOUNT_TOO_LARGE,
]);

export class ApiError extends Error {
  constructor(
    public readonly code: ApiErrorCode,
    message: string,
    public readonly status: number,
    public readonly errorId?: string,
    public readonly errorType?: string,
    // The raw structured detail from the response body, for callers that need
    // more than the message (e.g. the questions on a blocked cashflow seed).
    public readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  private static isKnownErrorCode(code?: string): code is keyof typeof ApiErrorCode {
    return code != null && code in ApiErrorCode;
  }

  static isGloballyHandledError(error: unknown): error is ApiError {
    return error instanceof ApiError && GLOBALLY_HANDLED_CODES.has(error.code);
  }

  static isExpectedValidationError(error: unknown): boolean {
    return error instanceof ApiError && EXPECTED_VALIDATION_CODES.has(error.code);
  }

  static isApiError(code: ApiErrorCode, error: unknown): error is ApiError {
    if (error instanceof ApiError && ApiErrorCode[error.code] === ApiErrorCode[code]) {
      return true;
    }

    return false;
  }

  static async fromApiResponse(response: Response) {
    let data: Record<string, unknown>;
    try {
      data = await response.json();
    } catch {
      return new ApiError(ApiErrorCode.UNPARSABLE_RESPONSE, 'An error occurred', response.status);
    }

    const errorId = typeof data.error_id === 'string' ? data.error_id : undefined;
    const errorType = typeof data.error_type === 'string' ? data.error_type : undefined;

    let errorMessage: string;
    let errorCode: string | undefined;
    let detailObject: Record<string, unknown> | undefined;

    if (typeof data.detail === 'string') {
      errorMessage = data.detail;
      errorCode = undefined;
    } else if (typeof data.detail === 'object' && data.detail !== null) {
      const detail = data.detail as Record<string, unknown>;
      detailObject = detail;
      errorMessage = typeof detail.message === 'string' ? detail.message : 'An error occurred';
      errorCode = typeof detail.error_code === 'string' ? detail.error_code : undefined;
    } else {
      errorMessage = typeof data.message === 'string' ? data.message : 'An error occurred';
      errorCode = undefined;
    }

    const code = ApiErrorCode[errorCode as keyof typeof ApiErrorCode] ?? ApiErrorCode.UNKNOWN_ERROR;

    if (this.isKnownErrorCode(errorCode)) {
      if (code === ApiErrorCode.DUPLICATE_FIELD) {
        const detail =
          typeof data.detail === 'object' && data.detail !== null
            ? (data.detail as Record<string, unknown>)
            : {};
        const field = typeof detail.field === 'string' ? detail.field : 'unknown';
        return new AfternoonDuplicateFieldApiError(
          errorMessage,
          response.status,
          field,
          errorId,
          errorType,
        );
      }
      if (code === ApiErrorCode.FIELD_SIZE_EXCEEDED) {
        const detail =
          typeof data.detail === 'object' && data.detail !== null
            ? (data.detail as Record<string, unknown>)
            : {};
        const field = typeof detail.field === 'string' ? detail.field : 'unknown';
        return new AfternoonFieldSizeExceededApiError(
          errorMessage,
          response.status,
          field,
          errorId,
          errorType,
        );
      }
      if (code === ApiErrorCode.AMOUNT_TOO_LARGE) {
        const detail =
          typeof data.detail === 'object' && data.detail !== null
            ? (data.detail as Record<string, unknown>)
            : {};
        const field = typeof detail.field === 'string' ? detail.field : 'unknown';
        return new AfternoonAmountTooLargeApiError(
          errorMessage,
          response.status,
          field,
          errorId,
          errorType,
        );
      }
      return new ApiError(
        ApiErrorCode[errorCode],
        errorMessage,
        response.status,
        errorId,
        errorType,
        detailObject,
      );
    }

    return new ApiError(code, errorMessage, response.status, errorId, errorType, detailObject);
  }
}

export class AfternoonDuplicateFieldApiError extends ApiError {
  constructor(
    message: string,
    public readonly status: number,
    public readonly field: string,
    errorId?: string,
    errorType?: string,
  ) {
    super(ApiErrorCode.DUPLICATE_FIELD, message, status, errorId, errorType);
  }

  static isDuplicateFieldError(error: unknown): error is AfternoonDuplicateFieldApiError {
    return (
      this.isApiError(ApiErrorCode.DUPLICATE_FIELD, error) &&
      error instanceof AfternoonDuplicateFieldApiError
    );
  }
}

export class AfternoonAmountTooLargeApiError extends ApiError {
  constructor(
    message: string,
    public readonly status: number,
    public readonly field: string,
    errorId?: string,
    errorType?: string,
  ) {
    super(ApiErrorCode.AMOUNT_TOO_LARGE, message, status, errorId, errorType);
  }

  static isAmountTooLargeError(error: unknown): error is AfternoonAmountTooLargeApiError {
    return (
      this.isApiError(ApiErrorCode.AMOUNT_TOO_LARGE, error) &&
      error instanceof AfternoonAmountTooLargeApiError
    );
  }
}

export class AfternoonFieldSizeExceededApiError extends ApiError {
  constructor(
    message: string,
    public readonly status: number,
    public readonly field: string,
    errorId?: string,
    errorType?: string,
  ) {
    super(ApiErrorCode.FIELD_SIZE_EXCEEDED, message, status, errorId, errorType);
  }

  static isFieldSizeExceededError(error: unknown): error is AfternoonFieldSizeExceededApiError {
    return (
      this.isApiError(ApiErrorCode.FIELD_SIZE_EXCEEDED, error) &&
      error instanceof AfternoonFieldSizeExceededApiError
    );
  }
}
